
import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

import {
    fetchBanner,
} from '../../service/service';

import {
    BannerApiResponse,
    BannerDashboardData,
    BannerDismissals,
    BannerViewer,
    DynamicBanner,
} from './bannerTypes';

import {
    getBannerRevision,
    getBannerStorageKey,
    readDismissals,
    saveDismissals,
} from './bannerUtils';

// -------------------------------------------------------
// Configuration
// -------------------------------------------------------

const REFRESH_INTERVAL_MS = 60 * 1000;
const MAX_TIMEOUT_MS = 2147483647;

// -------------------------------------------------------
// Types
// -------------------------------------------------------

interface BannerFetchState {
    viewerKey: string;
    data: BannerDashboardData | null;
    loading: boolean;
    error: string | null;
}

interface DismissalState {
    userId: string;
    values: BannerDismissals;
}

interface UseDynamicBannersResult {
    banners: DynamicBanner[];
    dashboardData: BannerDashboardData | null;
    dismissals: BannerDismissals;
    dismissalsReady: boolean;
    now: number;
    loading: boolean;
    error: string | null;
    dismissBanner: (banner: DynamicBanner) => void;
}

// -------------------------------------------------------
// Custom hook
// -------------------------------------------------------

export const useDynamicBanners = (
    viewer: BannerViewer | null,
    pathname: string
): UseDynamicBannersResult => {

    const userId = viewer?.id || '';

    // Keep a stable representation of the authenticated
    // user. This prevents unnecessary API calls when the
    // Header creates a new viewer object on each render.

    const viewerKey = viewer?.id
        ? JSON.stringify({
            id: viewer.id,
            capabilities: [...(viewer.capabilities || [])].sort((a, b) => a.localeCompare(b)),
            plan: viewer.plan || '',
            region: viewer.region || '',
        })
        : '';

    const requestKey = JSON.stringify([viewerKey, pathname]);

    // -----------------------------------------------------
    // 1. API response state
    // -----------------------------------------------------

    const [fetchState, setFetchState] =
        useState<BannerFetchState>({
            viewerKey: '',
            data: null,
            loading: false,
            error: null,
        });

    // -----------------------------------------------------
    // 2. Dismissed banner state
    // -----------------------------------------------------

    const [dismissalState, setDismissalState] =
        useState<DismissalState>({
            userId: '',
            values: {},
        });

    // Keep synchronous access to dismissal state so
    // multiple close actions cannot overwrite each other
    // before React finishes rendering.

    const dismissalsRef = useRef<DismissalState>({
        userId: '',
        values: {},
    });

    // -----------------------------------------------------
    // 3. Current timestamp for banner scheduling
    // -----------------------------------------------------

    const [now, setNow] = useState<number>(
        () => Date.now()
    );

    // -----------------------------------------------------
    // 4. Reset or load dismissals when user changes
    // -----------------------------------------------------

    useEffect(() => {
        const values = userId
            ? readDismissals(userId)
            : {};

        const nextState: DismissalState = {
            userId,
            values,
        };

        dismissalsRef.current = nextState;
        setDismissalState(nextState);

    }, [userId]);

    // -----------------------------------------------------
    // 5. Fetch banner configurations
    // -----------------------------------------------------

    useEffect(() => {
        // Do not fetch banners for unauthenticated users.

        if (!viewerKey) {
            setFetchState({
                viewerKey: '',
                data: null,
                loading: false,
                error: null,
            });

            return;
        }

        let disposed = false;

        let latestRequest = 0;

        // Retain already loaded banners during refresh.
        // Clear them only when the authenticated viewer
        // changes.

        setFetchState((previous) => ({
            viewerKey: requestKey,
            data:
                previous.viewerKey === requestKey
                    ? previous.data
                    : null,
            loading:
                previous.viewerKey !== requestKey,
            error: null,
        }));

        const refreshBanners = async () => {
            // Cancel any previous request.
            // The service cannot abort HTTP requests, so disregard older responses.
            // Ignore responses from older overlapping requests.
            const currentRequest = ++latestRequest;

            try {
                const rawResponse = await fetchBanner({
                    dashboardUrl: pathname,
                });

                // The existing service returns caught errors as values.
                // Validate the envelope rather than accepting an error object.
                const response = rawResponse as BannerApiResponse;

                if (
                    response?.success !== true ||
                    !Array.isArray(response?.data?.banners)
                ) {
                    throw new Error('Invalid PepWise banner API response');
                }

                // Ignore responses from cancelled requests
                // or components that have already unmounted.

                if (disposed || currentRequest !== latestRequest) {
                    return;
                }

                setFetchState({
                    viewerKey: requestKey,
                    data: response.data,
                    loading: false,
                    error: null,
                });

                // Refresh scheduling whenever API data changes.
                setNow(Date.now());

            } catch (error) {
                if (disposed || currentRequest !== latestRequest) {
                    return;
                }

                console.error(
                    'Failed to refresh PepWise banners:',
                    error
                );

                setFetchState((previous) => {
                    if (previous.viewerKey !== requestKey) {
                        return previous;
                    }

                    return {
                        ...previous,
                        loading: false,
                        error:
                            error instanceof Error
                                ? error.message
                                : 'Unable to fetch PepWise banners',
                    };
                });
            }
        };

        // Initial API request.
        void refreshBanners();

        // ---------------------------------------------------
        // Refresh every 60 seconds
        // ---------------------------------------------------

        const intervalId = window.setInterval(() => {
            if (document.visibilityState === 'visible') {
                void refreshBanners();
            }
        }, REFRESH_INTERVAL_MS);

        // ---------------------------------------------------
        // Refresh when returning to the browser tab
        // ---------------------------------------------------

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                setNow(Date.now());
                void refreshBanners();
            }
        };

        // ---------------------------------------------------
        // Refresh when browser regains focus
        // ---------------------------------------------------

        const handleWindowFocus = () => {
            setNow(Date.now());
            void refreshBanners();
        };

        document.addEventListener(
            'visibilitychange',
            handleVisibilityChange
        );

        window.addEventListener(
            'focus',
            handleWindowFocus
        );

        // ---------------------------------------------------
        // Cleanup
        // ---------------------------------------------------

        return () => {
            disposed = true;

            // Invalidate outstanding requests (the service has no AbortSignal).
            latestRequest += 1;

            window.clearInterval(intervalId);

            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange
            );

            window.removeEventListener(
                'focus',
                handleWindowFocus
            );
        };

    }, [viewerKey, pathname, requestKey]);

    // -----------------------------------------------------
    // 6. Only expose banners for the current viewer
    // -----------------------------------------------------

    const dashboardData: BannerDashboardData | null = Boolean(viewerKey) &&
        fetchState.viewerKey === requestKey
        ? fetchState.data
        : null;

    const banners = dashboardData?.banners || [];

    const loading = Boolean(viewerKey) && (
        fetchState.viewerKey !== requestKey ||
        fetchState.loading
    );

    const error =
        fetchState.viewerKey === requestKey
            ? fetchState.error
            : null;

    // -----------------------------------------------------
    // 7. Read dismissals only for the active user
    // -----------------------------------------------------

    const dismissalsReady =
        Boolean(userId) &&
        dismissalState.userId === userId;

    const dismissals: BannerDismissals =
        dismissalsReady
            ? dismissalState.values
            : {};

    // -----------------------------------------------------
    // 8. Automatically update banner schedule
    // -----------------------------------------------------

    useEffect(() => {
        if (!banners.length) {
            return;
        }

        const currentTime = Date.now();

        // Find the nearest scheduled start or end boundary.
        // Banner filtering in DynamicBannerOverlay will use
        // the updated 'now' value.

        const futureBoundaries: number[] = [];

        banners.forEach((banner) => {
            [banner.scheduling?.startTime, banner.scheduling?.endTime].forEach(
                (dateValue) => {
                    if (!dateValue) {
                        return;
                    }

                    const timestamp = Date.parse(dateValue);

                    if (
                        Number.isFinite(timestamp) &&
                        timestamp > currentTime
                    ) {
                        futureBoundaries.push(timestamp);
                    }
                }
            );
        });

        if (!futureBoundaries.length) {
            return;
        }

        const nextBoundary = Math.min(
            ...futureBoundaries
        );

        // Browser setTimeout has a maximum supported delay.
        // Long schedules are re-evaluated on subsequent ticks.

        const delay = Math.max(
            1,
            Math.min(
                nextBoundary - currentTime + 20,
                MAX_TIMEOUT_MS
            )
        );

        const timeoutId = window.setTimeout(() => {
            setNow(Date.now());
        }, delay);

        return () => {
            window.clearTimeout(timeoutId);
        };

    }, [banners, now]);

    // -----------------------------------------------------
    // 9. Dismiss a banner
    // -----------------------------------------------------

    const dismissBanner = useCallback(
        (banner: DynamicBanner) => {

            if (
                !userId ||
                !banner.bannerId ||
                !banner.isDismissible
            ) {
                return;
            }

            // Reuse the current in-memory values.
            // Read localStorage if the user has changed.

            const existing =
                dismissalsRef.current.userId === userId
                    ? dismissalsRef.current.values
                    : readDismissals(userId);

            const updated: BannerDismissals = {
                ...existing,

                [banner.bannerId]: {
                    viewed: 1,
                    revision: getBannerRevision(banner, dashboardData?.updatedAt),
                    dismissedAt: new Date().toISOString(),
                },
            };

            const nextState: DismissalState = {
                userId,
                values: updated,
            };

            // Update the reference synchronously.
            dismissalsRef.current = nextState;

            // Update React state immediately.
            setDismissalState(nextState);

            // Persist the dismissal across browser sessions.
            saveDismissals(userId, updated);

        },
        [userId, dashboardData?.updatedAt]
    );

    // -----------------------------------------------------
    // 10. Synchronize dismissal across browser tabs
    // -----------------------------------------------------

    useEffect(() => {
        if (!userId) {
            return;
        }

        const storageKey = getBannerStorageKey(userId);

        const handleStorageChange = (
            event: StorageEvent
        ) => {
            // Handle changes to this user's dismissal record.
            // Also handle localStorage.clear() events.

            if (
                event.key !== storageKey &&
                event.key !== null
            ) {
                return;
            }

            const latestValues = readDismissals(userId);

            const nextState: DismissalState = {
                userId,
                values: latestValues,
            };

            dismissalsRef.current = nextState;

            setDismissalState(nextState);
        };

        window.addEventListener(
            'storage',
            handleStorageChange
        );

        return () => {
            window.removeEventListener(
                'storage',
                handleStorageChange
            );
        };

    }, [userId]);

    // -----------------------------------------------------
    // 11. Return hook values
    // -----------------------------------------------------

    return {
        banners,
        dashboardData,
        dismissals,
        dismissalsReady,
        now,
        loading,
        error,
        dismissBanner,
    };
};
